import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Table, Checkbox as RSCheckbox, Pagination,
} from 'rsuite';
import { useSelector, useDispatch } from 'react-redux';
import { useAvailableHeight } from '@commonClient2/hooks';
import {
    applyUkgUserTimeZoneTransformation,
    // eslint-disable-next-line import/no-unresolved
} from '@commonClient2/services/ukgDateTime';
import LockIcon from '@material-ui/icons/Lock';
import { UPDATE_BID_PROFILE_MANAGEMENT } from '../../../store/bidProfileManagement';
import AwardedSchedule from './AwardedSchedule';
// eslint-disable-next-line no-unused-vars
import {
    UkgDateFormat,
    ScheduleSource,
    BidProcessType,
    BidProfileStatus,
    PeriodType,
    EmployeeBiddingStatus,
} from '../../../../Enums';
import { getEmployeeSubmissionStatusLabel } from '../../../../Enums/Helpers/LabellingHelper';
import DateFormatService from '../../../../services/DateFormatService';
import './EmployeeSelectionsTable.scss';
import NameCell from './NameCell';
import ExpandCell from './ExpandCell';
import { DAY_COLUMNS_COUNT } from '../../ScheduleSelection/ShiftlineTable/ShiftlineTable';

const { Column, HeaderCell, Cell } = Table;

const rowKey = 'employeeId';
const awardedScheduleHeaderHeight = 40;
const awardedScheduleRowPadding = 20;
const awardedScheduleShiftHeight = 35;
const awardedScheduleMinRowHeight = 55;
const paginationHeight = 65;

const pageSizeOptions = [10, 25, 50, 100, 200];
const initialPageSize = 10;

/**
 * @param {number} pageNumber
 * @param {number} totalCount
 * @param {number} pageSize
 * @returns number
 */
const autoFixOutOfBound = (pageNumber, totalCount, pageSize) => Math.min(Math.max(pageNumber, 1), Math.ceil(totalCount / pageSize));

/**
 * @type {EmployeeBiddingStatus[]}
 */
// @ts-ignore
const actionableStatus = [EmployeeBiddingStatus.Submitted, EmployeeBiddingStatus.Approved, EmployeeBiddingStatus.Refused];

const getPaginatedData = (data, pageSize, pageIndex) => (
    data.filter((v, i) => {
        const start = pageSize * (pageIndex - 1);
        const end = start + pageSize;
        return i >= start && i < end;
    })
);

/**
 * @typedef {'asc'|'desc'} SortType
 * @param {{
 *  data: BidProfileManagement.EmployeeSelectionsTableRowData[],
 *  sortColumn: string,
 *  setSortColumn: Function,
 *  sortType: SortType,
 *  setSortType: Function,
 *  fullNameObjectPath: string,
 *  rankObjectPath: string,
 *  openSingleEmployeePreferenceReport: Function,
 *  onBehalfSelected: Function,
 *  showScheduledDateAndTime: boolean,
 * }} props
 */
function EmployeeSelectionsTable({
    data,
    sortColumn,
    setSortColumn,
    sortType,
    setSortType,
    fullNameObjectPath,
    rankObjectPath,
    openSingleEmployeePreferenceReport = () => { },
    onBehalfSelected = () => { },
    showScheduledDateAndTime,
}) {
    const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem('employeeSelectionsTablePageSize') || `${initialPageSize}`, 10));
    const [pageIndex, setPageIndex] = useState(1);
    const [rowData, setRowData] = useState(getPaginatedData(data, pageSize, pageIndex));

    const { t } = useTranslation('shiftBidding', { keyPrefix: 'manager.bidProfileEdit.bidDashboard' });
    const ref = useRef(null);
    const totalCount = useRef(data.length);
    const tableRef = useRef(null);
    
    const height = (useAvailableHeight(ref) ?? 400) - paginationHeight;

    const dispatch = useDispatch();

    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const {
        bidProfileManagement, bidProfile, managerAppData,
    } = useSelector((state) => state);
    const { dataviewFields, dataviewEmployeeData } = managerAppData;
    const {
        expandedRowKeys,
        checkedKeys,
        isBidOnBehalfAllowed,
        isEditBidOnBehalfAllowed,
        isAwardingEmployees,
    } = bidProfileManagement;

    const {
        bidProfileInformation,
        maxScheduleRowItems,
        anchorDay,
        scheduleTemplates,
        isRebid,
        isPublishingToWorkplan,
        isManagementAllowed,
        isViewAllowed,
    } = bidProfile;

    const { shifts: shiftsDictionary, dayCount, periodType } = scheduleTemplates;
    const [employeeRankBiddingNow, setEmployeeRankBiddingNow] = useState(0);

    const isPreferential = bidProfile.originalBidProfileInformation.bidProcessType === BidProcessType.Preferential;

    const paginatedData = data.filter((v, i) => {
        const start = pageSize * (pageIndex - 1);
        const end = start + pageSize;
        return i >= start && i < end;
    });

    let isSelectAllChecked = false;
    let isSelectAllIndeterminate = false;

    if (checkedKeys.length === data.filter((x) => actionableStatus.includes(x.status)).length) {
        isSelectAllChecked = true;
    } else if (checkedKeys.length === 0) {
        isSelectAllChecked = false;
    } else if (checkedKeys.length > 0 && checkedKeys.length < data.filter((x) => actionableStatus.includes(x.status)).length) {
        isSelectAllIndeterminate = true;
    }

    const hasOnlyViewAccess = isViewAllowed && !isManagementAllowed;
    const isSequential = bidProfileInformation.bidProcessType === BidProcessType.Sequential;
    const isWorkplan = bidProfileInformation.scheduleSource === ScheduleSource.WorkplanManager;
    const isActiveWorkplan = isWorkplan && scheduleTemplates.isActiveWorkplanBid;
    const maxNbWeek = isWorkplan
        ? Math.max(...(scheduleTemplates.retrievedWorkplans || { workplans: [] })
            .workplans.map((x) => Math.max(...x.scheduleTemplates.items.map((
                /** @type {{ nbWeek: number; }} */ shiftLine,
            ) => shiftLine.nbWeek))))
        : Math.max(...scheduleTemplates.items.map((x) => x.nbWeek));
    const maxHeightUnits = Math.max(maxScheduleRowItems, maxNbWeek);
    const calculatedRowHeight = awardedScheduleHeaderHeight + awardedScheduleRowPadding + (maxHeightUnits * awardedScheduleShiftHeight);
    const awardedScheduleRowHeight = Math.max(calculatedRowHeight, awardedScheduleMinRowHeight);
    const expandableSectionHeight = awardedScheduleHeaderHeight + awardedScheduleRowHeight; // awarded schedule only has one row

    /**
     * @param {number} size 
     */
    const handleChangePageSize = (size) => {
        const newPageIndex = 1;
        setPageIndex(newPageIndex);
        setPageSize(size);
        setRowData(getPaginatedData(data, size, newPageIndex));
        localStorage.setItem('employeeSelectionsTablePageSize', `${size}`);
    };

    /**
     * 
     * @param {number} index
     */
    const handleChangePageIndex = (index) => {
        setPageIndex(index);
        setRowData(getPaginatedData(data, pageSize, index));
    };

    /**
     * @param {*} value
     * @param {boolean} checked
     */
    const handleCheckAll = (value, checked) => {
        const keys = checked
            // @ts-ignore
            ? paginatedData.filter((x) => actionableStatus.includes(x.status)).map((item) => item[rowKey])
            : [];

        dispatch({ type: UPDATE_BID_PROFILE_MANAGEMENT, payload: { checkedKeys: keys } });
    };
    /**
     * @param {*} value
     * @param {boolean} checked
     */
    const handleCheck = (value, checked) => {
        const keys = checked
            ? [...checkedKeys, value]
            : checkedKeys.filter((item) => item !== value);
        dispatch({ type: UPDATE_BID_PROFILE_MANAGEMENT, payload: { checkedKeys: keys } });
    };

    /**
     * @param {string} newSortColumn
     * @param {string} newSortType
     */
    const handleSortColumn = (newSortColumn, newSortType) => {
        setSortColumn(newSortColumn);
        setSortType(newSortType);
    };

    /**
     * @param {BidProfileManagement.EmployeeSelectionsTableRowData} rowData
     */
    const handleExpanded = (rowData) => {
        let open = false;
        const nextExpandedRowKeys = [];

        expandedRowKeys.forEach((key) => {
            if (key === rowData.employeeId) {
                open = true;
            } else {
                nextExpandedRowKeys.push(key);
            }
        });

        if (!open) {
            nextExpandedRowKeys.push(rowData.employeeId);
        }

        dispatch({ type: UPDATE_BID_PROFILE_MANAGEMENT, payload: { expandedRowKeys: nextExpandedRowKeys } });
    };

    /**
     * @param {BidProfileManagement.EmployeeSelectionsTableRowData} rowData
     */
    const renderRowExpanded = (rowData) => {
        if (!rowData.hasAwardedShiftline) {
            return t('noAwardedShiftline');
        }

        return (
            <AwardedSchedule
                scheduleTemplateItem={rowData.awardedShiftline}
                shiftsDictionary={shiftsDictionary}
                firstDayOfTheWeek={anchorDay}
                isWorkplan={isWorkplan}
                tableHeight={awardedScheduleRowHeight}
                dayCount={dayCount}
                isPeriodTypeDay={periodType === PeriodType.Days}
                daysPerPeriod={DAY_COLUMNS_COUNT}
            />
        );
    };

    /**
     * @param {string} col
     * @param {number} eId
     * @returns {string}
     */
    const getDataviewAttributeValue = (col, eId) => {
        const ee = dataviewEmployeeData.find((e) => e.employeeId === eId);
        const val = ee?.dataviewAttributes.find((x) => x.key === col);
        return val?.value || '';
    };
    const isBidOngoing = isSequential && [BidProfileStatus.Scheduled, BidProfileStatus.Active].includes(bidProfile.status);
    const findEmployeeRankBiddingNow = () => {
        const now = applyUkgUserTimeZoneTransformation(new Date());

        const employeesToBid = data.filter((x) => x.employeeSubmissionStartDate && x.employeeSubmissionStartDate <= now);
        if (employeesToBid.length) {
            const [employeesBiddingNow] = employeesToBid.slice(-1);
            if (employeeRankBiddingNow !== employeesBiddingNow.rank) {
                setEmployeeRankBiddingNow(employeesBiddingNow.rank);
            }
        }
    };

    useEffect(() => {
        const newPageIndex = autoFixOutOfBound(pageIndex, data.length, pageSize);
        setPageIndex(newPageIndex);
        const paginatedData = getPaginatedData(data, pageSize, newPageIndex);
        setRowData(paginatedData);
        totalCount.current = data.length;
    }, [data]);

    useEffect(() => {
        if (isBidOngoing) {
            const interval = setInterval(() => {
                findEmployeeRankBiddingNow();
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [employeeRankBiddingNow]);

    useEffect(() => {
        if (isBidOngoing) {
            findEmployeeRankBiddingNow();
        }
    }, []);

    useEffect(() => {
        if (tableRef.current && typeof tableRef.current.scrollLeft === 'function') {
            tableRef.current.scrollLeft(0);
        }
    }, [height]);

    return (
        <div
            className="employees-selection-table-container"
            ref={ref}
        >
            <Table
                ref={tableRef}
                key="bpm-employee-selections-table"
                height={height}
                shouldUpdateScroll={false}
                affixHeader
                data={rowData}
                rowClassName={(/** @type {BidProfileManagement.EmployeeSelectionsTableRowData} */
                    rowData,
                ) => {
                    let rowClassName = 'est-table-row';
                    if (employeeRankBiddingNow === rowData?.rank && bidProfile.status === BidProfileStatus.Active) {
                        rowClassName += ' employee-turn-active';
                    }

                    return rowClassName;
                }}
                className="bpm-table"
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
                rowKey={rowKey}
                expandedRowKeys={expandedRowKeys}
                renderRowExpanded={renderRowExpanded}
                rowExpandedHeight={expandableSectionHeight}
            >
                <Column
                    align="center"
                    fixed
                    resizable
                    width={50}
                >
                    <HeaderCell>
                        {!hasOnlyViewAccess && (
                            <RSCheckbox
                                inline
                                checked={isSelectAllChecked}
                                indeterminate={isSelectAllIndeterminate}
                                onChange={handleCheckAll}
                            />
                        )}
                    </HeaderCell>
                    <Cell>
                        {/** @param {BidProfileManagement.EmployeeSelectionsTableRowData} rowData */
                            (rowData) => (
                                (!hasOnlyViewAccess) && (
                                    <div style={{ lineHeight: '46px' }}>
                                        <RSCheckbox
                                            value={rowData.employeeId}
                                            inline
                                            onChange={handleCheck}
                                            checked={checkedKeys.some((item) => item === rowData.employeeId)}
                                            disabled={!actionableStatus.includes(rowData.status)}
                                        />
                                    </div>
                                )
                            )
                        }
                    </Cell>
                </Column>
                <Column
                    key="name"
                    fixed
                    sortable
                    resizable
                    flexGrow={1}
                    minWidth={150}
                >
                    <HeaderCell>{t('columns.name')}</HeaderCell>
                    <NameCell
                        dataKey={fullNameObjectPath}
                        dataviewEmployeeData={dataviewEmployeeData}
                    />
                </Column>
                {
                    dataviewFields
                        .filter((x) => x.checked)
                        .map((element) => (
                            <Column
                                key={element.key}
                                resizable
                                flexGrow={1}
                                minWidth={150}
                            >
                                <HeaderCell>{element.label}</HeaderCell>
                                <Cell>
                                    {
                                        /** @param {BidProfileManagement.EmployeeSelectionsTableRowData} rowData */
                                        (rowData) => getDataviewAttributeValue(element.key, rowData.employeeId)
                                    }
                                </Cell>
                            </Column>
                        ))
                }
                <Column
                    key="rank"
                    sortable
                    resizable
                    flexGrow={1}
                    minWidth={125}
                >
                    <HeaderCell>{t('columns.rank')}</HeaderCell>
                    <Cell dataKey={rankObjectPath}>
                        {
                            (/** @type { BidProfileManagement.EmployeeSelectionsTableRowData } */ rowData) => (rowData.isLocked
                                ? (
                                    <div className="est-lock-icon">
                                        <LockIcon />
                                    </div>
                                )
                                : (rowData.rank))
                        }
                    </Cell>
                </Column>
                {showScheduledDateAndTime && (
                    <Column
                        resizable
                        key="scheduledDateAndTime"
                        flexGrow={1}
                        minWidth={300}
                    >
                        <HeaderCell>Scheduled date and time</HeaderCell>
                        <Cell dataKey="scheduledDateAndTime" />
                    </Column>
                )}
                <Column
                    key="status"
                    sortable
                    resizable
                    flexGrow={1}
                    minWidth={150}
                >
                    <HeaderCell>{t('columns.status')}</HeaderCell>
                    <Cell dataKey="status">
                        {
                            (/** @type { BidProfileManagement.EmployeeSelectionsTableRowData } */ rowData) =>
                                getEmployeeSubmissionStatusLabel(rowData.status)
                        }
                    </Cell>
                </Column>
                <Column
                    key="submittedOn"
                    sortable
                    resizable
                    flexGrow={1}
                    minWidth={150}
                >
                    <HeaderCell>{t('columns.submittedOn')}</HeaderCell>
                    <Cell dataKey="submittedOn">
                        {
                            (/** @type { BidProfileManagement.EmployeeSelectionsTableRowData } */ rowData) =>
                                DateFormatService.formatDateOrEmpty(rowData.submittedOn, UkgDateFormat.LongDateTime)
                        }
                    </Cell>
                </Column>
                <Column
                    key="submittedBy"
                    sortable
                    resizable
                    flexGrow={1}
                    minWidth={150}
                >
                    <HeaderCell>{t('columns.submittedBy')}</HeaderCell>
                    <Cell dataKey="submittedBy" />
                </Column>
                {(isRebid || isActiveWorkplan) && (
                    <Column
                        key="currentShiftLineAssignment"
                        sortable
                        resizable
                        flexGrow={1}
                        minWidth={150}
                    >
                        <HeaderCell>{t('columns.currentAssignment')}</HeaderCell>
                        <Cell dataKey="currentShiftLineAssignment" />
                    </Column>
                )}
                {isPreferential && (
                    <Column
                        key="bidPreference"
                        sortable
                        resizable
                        flexGrow={1}
                        minWidth={150}
                    >
                        <HeaderCell>{t('columns.bidPreference')}</HeaderCell>
                        <Cell className="bdb-cell-awarded-schedule">
                            {
                                // @ts-ignore
                                (/** @type { BidProfileManagement.EmployeeSelectionsTableRowData } */ rowData) => (
                                    <div>
                                        <p className="awarded-schedule-selection">
                                            {(
                                                rowData.employeeSelectionIndex > 0 && rowData.employeeSelectionIndex
                                            )}
                                        </p>
                                    </div>
                                )
                            }
                        </Cell>
                    </Column>
                )}
                <Column
                    key="awardedSchedule"
                    sortable
                    resizable
                    flexGrow={1}
                    minWidth={150}
                >
                    <HeaderCell>{t('columns.newAssignment')}</HeaderCell>
                    <Cell className="bdb-cell-awarded-schedule">
                        {(/** @type { BidProfileManagement.EmployeeSelectionsTableRowData } */ rowData) => ((rowData.hasAwardedShiftline)
                            ? (
                                <div className={`${isWorkplan ? 'bdb-content-awarded-schedule' : ''}`}>
                                    <p className="awarded-schedule-name">{rowData.name}</p>
                                    {
                                        isWorkplan ? (
                                            <p className="awarded-schedule-source">
                                                {`${rowData.awardedShiftline.workplanName}`}
                                            </p>
                                        ) : null
                                    }
                                </div>
                            )
                            : ''
                        )}
                    </Cell>
                </Column>
                <Column
                    align="center"
                    fixed="right"
                    width={200}
                    minWidth={200}
                >
                    <HeaderCell />
                    <ExpandCell
                        expandedRowKeys={expandedRowKeys}
                        onChange={handleExpanded}
                        onEmployeeChoicesSelected={openSingleEmployeePreferenceReport}
                        onBehalfSelected={onBehalfSelected}
                        canViewEmployeeChoices={!isPublishingToWorkplan}
                        isScheduled={bidProfile.status === BidProfileStatus.Scheduled}
                        isBidOnBehalfAllowed={isBidOnBehalfAllowed && !isPublishingToWorkplan}
                        isBidOnBehalfDisabled={isAwardingEmployees}
                        isEditBidOnBehalfAllowed={isEditBidOnBehalfAllowed && !isPublishingToWorkplan}
                        isEditBidOnBehalfDisabled={isAwardingEmployees}
                        hasOnlyViewAccess={hasOnlyViewAccess}
                        t={t}
                    />
                </Column>
            </Table>
            <div className="pagination-wrapper">
                <Pagination
                    prev
                    next
                    first
                    last
                    ellipsis
                    boundaryLinks
                    maxButtons={5}
                    size="xs"
                    layout={['total', '-', 'pager', 'skip', '|', 'limit']}
                    total={totalCount.current}
                    limitOptions={pageSizeOptions}
                    limit={pageSize}
                    onChangeLimit={handleChangePageSize}
                    activePage={pageIndex}
                    onChangePage={handleChangePageIndex}
                />
            </div>
        </div>

    );
}

export default EmployeeSelectionsTable;
